import { useEffect, useMemo, useState } from 'react';
import {
  buildInspectionReportSummary,
  formatReportStatus,
  reportDeveloperLabel,
  reportProjectLabel,
} from '../lib/report';
import { createEvidencePreviewUrl } from '../services/issues';
import { loadInspectionReportSnapshot } from '../services/report';
import type { InspectionReportSnapshot } from '../types/report';

interface ReportWorkspaceProps {
  ownerId: string;
  assessmentId: string;
  onBack: () => void;
}

type ReportMode = 'buyer' | 'builder';

function formatDate(value: string | null | undefined): string {
  if (!value) return 'Not recorded';
  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: value.includes('T') ? 'short' : undefined,
  }).format(new Date(value));
}

export function ReportWorkspace({ ownerId, assessmentId, onBack }: ReportWorkspaceProps) {
  const [snapshot, setSnapshot] = useState<InspectionReportSnapshot | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<ReportMode>('buyer');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setStatus('loading');
      setError(null);
      try {
        const next = await loadInspectionReportSnapshot(ownerId, assessmentId);
        if (!cancelled) {
          setSnapshot(next);
          setStatus('ready');
        }
      } catch (caught) {
        if (!cancelled) {
          setStatus('error');
          setError(caught instanceof Error ? caught.message : 'Could not load report data.');
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [assessmentId, ownerId]);

  const summary = useMemo(
    () => (snapshot ? buildInspectionReportSummary(snapshot) : null),
    [snapshot],
  );

  async function openEvidence(storagePath: string) {
    try {
      const url = await createEvidencePreviewUrl(storagePath);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not open the private evidence.');
    }
  }

  if (status === 'loading') {
    return <main className="page report-page"><div className="status-banner">Building report from saved inspection data…</div></main>;
  }

  if (status === 'error' || !snapshot || !summary) {
    return (
      <main className="page report-page">
        <button className="text-button no-print" type="button" onClick={onBack}>← Back to findings</button>
        <div className="status-banner" role="alert">{error ?? 'Report data is unavailable.'}</div>
      </main>
    );
  }

  const builderActions = snapshot.issues.filter((issue) => issue.action);

  return (
    <main className="page report-page">
      <div className="report-toolbar no-print">
        <button className="text-button" type="button" onClick={onBack}>← Back to findings</button>
        <div className="report-mode-switch" aria-label="Report type">
          <button type="button" className={mode === 'buyer' ? 'filter-chip filter-chip-active' : 'filter-chip'} onClick={() => setMode('buyer')}>Buyer report</button>
          <button type="button" className={mode === 'builder' ? 'filter-chip filter-chip-active' : 'filter-chip'} onClick={() => setMode('builder')}>Builder action pack</button>
        </div>
        <button className="button button-primary" type="button" onClick={() => window.print()}>Print / Save PDF</button>
      </div>

      {error ? <div className="status-banner no-print" role="alert">{error}</div> : null}

      <section className="report-sheet">
        <header className="report-header">
          <div>
            <span className="eyebrow">{mode === 'buyer' ? 'Buyer inspection record' : 'Builder action pack'}</span>
            <h1>{reportProjectLabel(snapshot)}</h1>
            <p>{reportDeveloperLabel(snapshot)} · {snapshot.property.locality ?? 'Hyderabad'}</p>
          </div>
          <div className="report-generated">
            <strong>NivasaSaathi</strong>
            <span>Generated {formatDate(snapshot.generatedAt)}</span>
          </div>
        </header>

        <section className="report-property-grid">
          <div><span>Property</span><strong>{formatReportStatus(snapshot.property.property_type)}</strong></div>
          <div><span>Unit</span><strong>{snapshot.property.unit_or_villa_number ?? 'Not recorded'}</strong></div>
          <div><span>Tower / phase</span><strong>{snapshot.property.tower_or_block ?? 'Not recorded'}</strong></div>
          <div><span>Inspection stage</span><strong>{formatReportStatus(snapshot.property.inspection_stage)}</strong></div>
          <div><span>Bedrooms</span><strong>{snapshot.property.bedrooms ?? '—'}</strong></div>
          <div><span>Bathrooms</span><strong>{snapshot.property.bathrooms ?? '—'}</strong></div>
        </section>

        {mode === 'buyer' ? (
          <>
            <section className="report-stat-grid">
              <div><strong>{summary.answeredChecks}</strong><span>Checks answered</span></div>
              <div><strong>{summary.totalFindings}</strong><span>Findings</span></div>
              <div><strong>{summary.openFindings}</strong><span>Still open</span></div>
              <div><strong>{summary.closedFindings}</strong><span>Closed with evidence</span></div>
              <div><strong>{summary.waivedFindings}</strong><span>Waived</span></div>
              <div><strong>{summary.urgentFindings}</strong><span>Immediate / urgent</span></div>
            </section>

            <section className="report-section">
              <h2>Findings, actions and closure state</h2>
              {snapshot.issues.length === 0 ? <p>No findings were recorded in this inspection.</p> : null}
              {snapshot.issues.map((issue, index) => (
                <article className="report-finding" key={issue.id}>
                  <div className="report-finding-heading">
                    <span>Finding {index + 1}</span>
                    <span className={`priority-chip priority-${issue.priority}`}>{issue.priority}</span>
                    <span className="report-status-chip">{formatReportStatus(issue.status)}</span>
                  </div>
                  <h3>{issue.title}</h3>
                  <p><strong>Location:</strong> {issue.location}</p>
                  {issue.description ? <p>{issue.description}</p> : null}

                  {issue.evidence.length > 0 ? (
                    <div className="report-subsection">
                      <strong>Evidence</strong>
                      <ul>
                        {issue.evidence.map((item, evidenceIndex) => (
                          <li key={item.id}>
                            {formatReportStatus(item.evidence_type)} photo {evidenceIndex + 1}
                            {item.note ? `, ${item.note}` : ''}
                            <button className="text-button no-print" type="button" onClick={() => void openEvidence(item.storage_path)}>Open private photo</button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {issue.action ? (
                    <div className="report-action-card">
                      <strong>Corrective action</strong>
                      <p>{issue.action.requested_action}</p>
                      <dl>
                        <div><dt>Owner</dt><dd>{formatReportStatus(issue.action.accountable_party)}{issue.action.accountable_party_name ? `, ${issue.action.accountable_party_name}` : ''}</dd></div>
                        <div><dt>Due</dt><dd>{formatDate(issue.action.due_date)}</dd></div>
                        <div><dt>Status</dt><dd>{formatReportStatus(issue.action.status)}</dd></div>
                      </dl>
                    </div>
                  ) : (
                    <p className="report-muted">No corrective action has been created for this finding.</p>
                  )}

                  {issue.actionEvents.length > 0 ? (
                    <div className="report-subsection report-timeline">
                      <strong>Action history</strong>
                      <ol>
                        {issue.actionEvents.map((event) => (
                          <li key={event.id}>
                            <span>{formatDate(event.created_at)}</span>
                            <b>{formatReportStatus(event.event_type)}</b>
                            {event.note ? <p>{event.note}</p> : null}
                          </li>
                        ))}
                      </ol>
                    </div>
                  ) : null}

                  {issue.closureEvents.length > 0 ? (
                    <div className="report-subsection">
                      <strong>Closure record</strong>
                      {issue.closureEvents.map((closure) => (
                        <p key={closure.id}>
                          {closure.closure_evidence_id ? 'Closed with buyer-recorded closure evidence.' : 'Closed through explicit evidence waiver.'}
                          {closure.waiver_reason ? ` Waiver reason: ${closure.waiver_reason}` : ''}
                          {closure.closure_note ? ` Note: ${closure.closure_note}` : ''}
                        </p>
                      ))}
                    </div>
                  ) : null}
                </article>
              ))}
            </section>
          </>
        ) : (
          <section className="report-section builder-pack-section">
            <h2>Corrections requested from accountable parties</h2>
            <p className="report-muted">This pack contains buyer-recorded findings and requested corrective actions only. It does not represent delivery, acknowledgement, certification or professional acceptance unless those states are explicitly recorded below.</p>
            {builderActions.length === 0 ? <p>No builder-facing actions have been created.</p> : null}
            {builderActions.map((issue, index) => (
              <article className="builder-pack-item" key={issue.id}>
                <div className="builder-pack-number">{String(index + 1).padStart(2, '0')}</div>
                <div>
                  <div className="report-finding-heading">
                    <span className={`priority-chip priority-${issue.action!.priority}`}>{issue.action!.priority}</span>
                    <span className="report-status-chip">{formatReportStatus(issue.action!.status)}</span>
                  </div>
                  <h3>{issue.title}</h3>
                  <p><strong>Location:</strong> {issue.location}</p>
                  <p><strong>Requested correction:</strong> {issue.action!.requested_action}</p>
                  <p><strong>Accountable party:</strong> {formatReportStatus(issue.action!.accountable_party)}{issue.action!.accountable_party_name ? `, ${issue.action!.accountable_party_name}` : ''}</p>
                  <p><strong>Due date:</strong> {formatDate(issue.action!.due_date)}</p>
                  {issue.action!.follow_up_date ? <p><strong>Follow-up:</strong> {formatDate(issue.action!.follow_up_date)}</p> : null}
                </div>
              </article>
            ))}
          </section>
        )}

        <footer className="report-disclaimer">
          <strong>Important limitation</strong>
          <p>This record organises buyer-observable checks, notes, evidence and follow-up. It is not a structural, civil, electrical, plumbing, fire-safety or legal certification, and it does not replace inspection or advice from appropriately qualified professionals.</p>
        </footer>
      </section>
    </main>
  );
}
